import React, { useEffect, useRef } from 'react';
import { Box, Typography, LinearProgress, Stack, Alert, Skeleton } from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Carwash } from '../../types/CarwashType';
import PartnerCard from './PartnerCard';

function PartnerCardSkeleton() {
  return (
    <Box sx={{ borderRadius: 2, bgcolor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', mb: 1.5, px: 1.5, py: 1.25 }}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Skeleton variant="rounded" width={44} height={44} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton width="58%" height={14} sx={{ mb: 0.5 }} />
          <Skeleton width="80%" height={12} sx={{ mb: 0.5 }} />
          <Stack direction="row" spacing={0.75}>
            <Skeleton width={44} height={16} sx={{ borderRadius: 4 }} />
            <Skeleton width={28} height={12} />
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

interface Props {
  carwashes: Carwash[];
  loading: boolean;
  error: string | null;
  selectedCarwashId: number | null;
  onCarwashSelect: (carwash: Carwash) => void;
}

function PartnerList({ carwashes, loading, error, selectedCarwashId, onCarwashSelect }: Props) {
  const selectedRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedCarwashId]);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, position: 'relative' }}>
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
          {loading ? 'Buscando parceiros...' : `${carwashes.length} parceiro${carwashes.length !== 1 ? 's' : ''} encontrado${carwashes.length !== 1 ? 's' : ''}`}
        </Typography>
        {loading && carwashes.length > 0 && (
          <LinearProgress
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 2,
              bgcolor: 'rgba(0,180,228,0.12)',
              '& .MuiLinearProgress-bar': { bgcolor: '#00b4e4' },
            }}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1 }}>
        {loading && carwashes.length === 0 && (
          <>
            {Array.from({ length: 3 }).map((_, i) => (
              <PartnerCardSkeleton key={i} />
            ))}
          </>
        )}

        {!loading && carwashes.length === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 6, color: 'text.secondary' }}>
            <SearchOffIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
            <Typography variant="body2" textAlign="center">
              Nenhum parceiro encontrado nesta área.
              <br />
              Tente mover o mapa ou ajustar os filtros.
            </Typography>
          </Box>
        )}

        {carwashes.map((carwash) => (
          <Box
            key={carwash.id}
            ref={carwash.id === selectedCarwashId ? selectedRef : null}
          >
            <PartnerCard
              carwash={carwash}
              selected={carwash.id === selectedCarwashId}
              onClick={onCarwashSelect}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default PartnerList;
